import { useEffect, useMemo, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronDown, ClipboardList, PenLine, Star } from "lucide-react";
import {
  CHECKLIST_TEMPLATES,
  getChecklistTemplate,
  matchTemplateRoomToProperty,
  type TemplateTask,
} from "@/data/checklistTemplates";

interface Step4ChecklistProps {
  propertyRooms: string[];
  selectedTemplate: string | null;
  customTasksJson: string;
  propertyType: string;
  onSelectTemplate: (templateId: string) => void;
  onTasksChange: (tasksJson: string) => void;
  onModeChange?: (mode: "template" | "custom" | null) => void;
}

type TaskSelection = { key: string; room: string; text: string; checked: boolean };

function taskKey(room: string, text: string) {
  return `${room}::${text}`;
}

function parseTaskSelections(json: string, tasks: TemplateTask[]): TaskSelection[] {
  if (json) {
    try {
      const parsed = JSON.parse(json) as TaskSelection[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch {
      /* fall through */
    }
  }
  return tasks.map((t) => ({
    key: taskKey(t.room, t.text),
    room: t.room,
    text: t.text,
    checked: true,
  }));
}

function ChoiceCard({
  title,
  description,
  icon: Icon,
  onClick,
}: {
  title: string;
  description: string;
  icon: typeof ClipboardList;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-3 p-5 rounded-xl border-2 border-border text-left",
        "hover:border-primary/40 hover:bg-muted/40 transition-all"
      )}
    >
      <Icon className="h-6 w-6 text-primary" />
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>
    </button>
  );
}

export function Step4Checklist({
  propertyRooms,
  selectedTemplate,
  customTasksJson,
  propertyType,
  onSelectTemplate,
  onTasksChange,
  onModeChange,
}: Step4ChecklistProps) {
  const [mode, setMode] = useState<"template" | "custom" | null>(null);
  const template = selectedTemplate ? getChecklistTemplate(selectedTemplate) : null;
  const [open, setOpen] = useState(true);

  const setModeAndNotify = (next: "template" | "custom" | null) => {
    setMode(next);
    onModeChange?.(next);
  };

  const selections = useMemo(() => {
    if (!template) return [];
    return parseTaskSelections(customTasksJson, template.tasks);
  }, [customTasksJson, template]);

  useEffect(() => {
    if (!template || customTasksJson) return;
    onTasksChange(
      JSON.stringify(
        template.tasks.map((t) => ({
          key: taskKey(t.room, t.text),
          room: t.room,
          text: t.text,
          checked: true,
        }))
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init selections when template changes
  }, [selectedTemplate]);

  const grouped = useMemo(() => {
    const map = new Map<string, TaskSelection[]>();
    for (const s of selections) {
      const propRoom = matchTemplateRoomToProperty(s.room, propertyRooms);
      if (!map.has(propRoom)) map.set(propRoom, []);
      map.get(propRoom)!.push(s);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [selections, propertyRooms]);

  const toggleTask = (key: string, checked: boolean) => {
    const next = selections.map((s) => (s.key === key ? { ...s, checked } : s));
    onTasksChange(JSON.stringify(next));
  };

  const taskCountForTemplate = (id: string) => {
    const t = getChecklistTemplate(id);
    if (!t) return 0;
    return t.tasks.length;
  };

  const showTurnoverRecommended =
    propertyType === "house" || propertyType === "villa" || propertyType === "townhouse";

  if (mode === null) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Cleaning checklist</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Use a template or build your own after setup.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ChoiceCard
            title="Use a template"
            description="Pick a pre-made checklist and deselect anything you don't need."
            icon={ClipboardList}
            onClick={() => setModeAndNotify("template")}
          />
          <ChoiceCard
            title="Build my own"
            description="Start from scratch and add tasks room by room."
            icon={PenLine}
            onClick={() => setModeAndNotify("custom")}
          />
        </div>
      </div>
    );
  }

  if (mode === "custom") {
    return (
      <div className="space-y-6">
        <div>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-muted-foreground"
            onClick={() => setModeAndNotify(null)}
          >
            ← Change
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">Build your own checklist</h1>
          <p className="text-muted-foreground text-sm mt-3">
            You can build your checklist from the property page after setup completes. Click
            Continue to skip for now — your rooms are already saved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-muted-foreground"
          onClick={() => setModeAndNotify(null)}
        >
          ← Change
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight mt-2">Cleaning checklist</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Start with a template and uncheck anything you do not need. You can edit tasks later.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {CHECKLIST_TEMPLATES.map((t) => {
          const selected = selectedTemplate === t.id;
          const count = taskCountForTemplate(t.id);
          const roomCount = propertyRooms.length || 7;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelectTemplate(t.id)}
              className={cn(
                "rounded-xl border-2 p-4 text-left transition-all",
                selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
              )}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{t.name}</span>
                {t.id === "turnover" && showTurnoverRecommended && (
                  <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 px-2 py-0.5 rounded-full">
                    <Star className="h-3 w-3" /> recommended
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{t.description}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {count} tasks across {Math.min(roomCount, count)} rooms
              </p>
            </button>
          );
        })}
      </div>

      {template && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium">
            Preview tasks by room
            <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-3">
            {grouped.map(([roomName, tasks]) => (
              <div key={roomName} className="rounded-lg border p-3">
                <p className="text-sm font-medium mb-2">{roomName}</p>
                <ul className="space-y-2">
                  {tasks.map((task) => (
                    <li key={task.key} className="flex items-start gap-2">
                      <Checkbox
                        id={task.key}
                        checked={task.checked}
                        onCheckedChange={(v) => toggleTask(task.key, v === true)}
                      />
                      <label htmlFor={task.key} className="text-sm leading-snug cursor-pointer">
                        {task.text}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
